/**
 * Como cada valor de enum de orçamentos se lê na tela.
 *
 * Papel: aqui ficam os três mapas que traduzem a situação da proposta, a
 * natureza do que se orça e o modo de pagamento para o texto que aparece na
 * tela, e `TOM_DO_STATUS`, que dá a cada situação a variante de `Badge`
 * correspondente.
 *
 * Como usar: um valor novo em `STATUS_ORCAMENTO`, em `CATEGORIA_ORCAMENTO` ou
 * em `MODO_PAGAMENTO_ORCAMENTO` precisa de uma linha em cada mapa daqui. Eles
 * são `Record` do tipo do enum, e não objetos soltos, para que acrescentar o
 * valor no contrato e esquecer aqui vire erro de typecheck em vez de uma célula
 * vazia na tabela.
 *
 * `nao_aprovado` e `cancelado` têm rótulos que dizem quem decidiu: a proposta
 * recusada foi rejeitada pela família, e a cancelada foi desistência antes da
 * decisão. Quem for pedir um segundo orçamento ao mesmo fornecedor precisa
 * distinguir os dois.
 */
import {
  type CategoriaOrcamento,
  type ModoPagamentoOrcamento,
  type StatusOrcamento,
} from '@concierge/shared';

/**
 * Em que ponto a proposta está.
 *
 * `orcamento` é o rótulo literal — "Orçamento", sem "Em" na frente —, o texto
 * que a pilula da linha e a opção do seletor de situação mostram.
 */
export const ROTULOS_STATUS: Record<StatusOrcamento, string> = {
  orcamento: 'Orçamento',
  aprovado: 'Aprovado',
  nao_aprovado: 'Não aprovado',
  cancelado: 'Cancelado',
};

/**
 * A variante de `Badge` de cada situação.
 *
 * `aprovado` é a única que chega a `concluido`; `nao_aprovado` e `cancelado`
 * vão para `vencido` porque as duas encerram a proposta sem seguir adiante, e
 * `orcamento`, ainda em aberto, fica em `pendente`.
 */
export const TOM_DO_STATUS: Record<StatusOrcamento, 'pendente' | 'concluido' | 'vencido'> = {
  orcamento: 'pendente',
  aprovado: 'concluido',
  nao_aprovado: 'vencido',
  cancelado: 'vencido',
};

/**
 * A natureza do que está sendo orçado.
 *
 * `Outros` é o rótulo do valor que toda proposta recebe ao nascer, e por isso
 * ele conta as duas coisas juntas: a que foi classificada e não coube nas
 * outras quatro, e a que ninguém classificou.
 */
export const ROTULOS_CATEGORIA: Record<CategoriaOrcamento, string> = {
  reforma: 'Reforma',
  manutencao: 'Manutenção',
  evento: 'Evento',
  servico: 'Serviço',
  outros: 'Outros',
};

/** Como a proposta seria paga. */
export const ROTULOS_PAGAMENTO: Record<ModoPagamentoOrcamento, string> = {
  a_vista: 'À vista',
  parcelado: 'Parcelado',
};
